import { supabase } from '@/integrations/supabase/client';

export interface FollowResult {
  success: boolean;
  action?: 'followed' | 'unfollowed';
  error?: any;
}

export class FollowService {
  /**
   * Follow a user directly (for public profiles)
   */
  static async followUser(targetUserId: string): Promise<FollowResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { error } = await supabase
        .from('follows')
        .insert({
          follower_id: session.user.id,
          following_id: targetUserId,
        });

      if (error) {
        console.error('Error following user:', error);
        return { success: false, error };
      }

      return { success: true, action: 'followed' };
    } catch (error) {
      console.error('Unexpected error following user:', error);
      return { success: false, error };
    }
  }

  /**
   * Unfollow a user
   */
  static async unfollowUser(targetUserId: string): Promise<FollowResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { error } = await supabase
        .from('follows')
        .delete()
        .eq('follower_id', session.user.id)
        .eq('following_id', targetUserId);

      if (error) {
        console.error('Error unfollowing user:', error);
        return { success: false, error };
      }

      return { success: true, action: 'unfollowed' };
    } catch (error) {
      console.error('Unexpected error unfollowing user:', error);
      return { success: false, error };
    }
  }

  /**
   * Check if current user follows target user
   */
  static async checkIsFollowing(targetUserId: string): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return false;

      const { data, error } = await supabase
        .from('follows')
        .select('id')
        .eq('follower_id', session.user.id)
        .eq('following_id', targetUserId)
        .maybeSingle();

      if (error) {
        console.error('Error checking follow status:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Unexpected error checking follow status:', error);
      return false;
    }
  }

  /**
   * Get follow counts for a user
   */
  static async getFollowCounts(userId: string): Promise<{ followers: number; following: number }> {
    try {
      const [followersResult, followingResult] = await Promise.all([
        supabase
          .from('follows')
          .select('id', { count: 'exact', head: true })
          .eq('following_id', userId),
        supabase
          .from('follows')
          .select('id', { count: 'exact', head: true })
          .eq('follower_id', userId),
      ]);

      return {
        followers: followersResult.count || 0,
        following: followingResult.count || 0,
      };
    } catch (error) {
      console.error('Error getting follow counts:', error);
      return { followers: 0, following: 0 };
    }
  }
}